import {
    ButtonPrimary,
    Card,
    Colors,
    ConfirmDialog,
    Container,
    defaultHover,
    Heading,
    Icon,
    PageContainer,
    Text,
} from '@amelio/ui-library';
import { useState } from 'react';

import type { User } from '@/types/directory/User';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';

import { CreateSurveyTemplateArrowIllustration } from '@/components/Illustrations/Illustration';
import { NewTemplateCard } from '@/components/TemplateCard/NewTemplateCard';
import { TemplateCard } from '@/components/TemplateCard/TemplateCard';
import { TemplateCardSkeleton } from '@/components/TemplateCard/TemplateCardSkeleton';
import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getLocalizedText } from '@/utils/localization';

interface SelectTemplateProps {
    isSelfEvaluation: boolean;
    templates: ReviewTemplate[];
    hasMoreTemplates: boolean;
    isFetchingTemplates: boolean;
    isFetchingNextPage: boolean;
    selectedTemplate: ReviewTemplate | undefined;
    initialTemplateId?: string;
    shouldAllowTemplateManagement?: boolean;
    onSelectTemplate: (template: ReviewTemplate) => void;
    onUseTemplate?: (template: ReviewTemplate) => void;
    onStartBuilding: () => void;
    onClearTemplate?: () => Promise<void>;
    loadMore: () => void;
    onManageTemplate?: () => void;
    previewEvaluee?: User;
}

export const SelectTemplate = ({
    isSelfEvaluation,
    templates,
    hasMoreTemplates,
    isFetchingTemplates,
    isFetchingNextPage,
    selectedTemplate,
    initialTemplateId,
    shouldAllowTemplateManagement = false,
    onManageTemplate,
    loadMore,
    onSelectTemplate,
    onUseTemplate,
    onStartBuilding,
    onClearTemplate,
    previewEvaluee,
}: SelectTemplateProps) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();
    const [pendingTemplate, setPendingTemplate] = useState<ReviewTemplate | undefined>(undefined);
    const [shouldAdvanceAfterConfirm, setShouldAdvanceAfterConfirm] = useState(false);
    const [pendingStartBuilding, setPendingStartBuilding] = useState(false);

    const isOverwritingPersistedTemplate = (template: ReviewTemplate): boolean =>
        Boolean(initialTemplateId && template.id && template.id !== initialTemplateId);

    const handleTemplateClick = (template: ReviewTemplate) => {
        if (isOverwritingPersistedTemplate(template)) {
            setPendingTemplate(template);
            setShouldAdvanceAfterConfirm(false);
        } else {
            onSelectTemplate(template);
        }
    };

    const handleUseTemplate = (template: ReviewTemplate) => {
        if (!onUseTemplate) {
            handleTemplateClick(template);
            return;
        }

        if (isOverwritingPersistedTemplate(template)) {
            setPendingTemplate(template);
            setShouldAdvanceAfterConfirm(true);
        } else {
            onUseTemplate(template);
        }
    };

    const onConfirmSwitch = () => {
        if (pendingTemplate) {
            if (shouldAdvanceAfterConfirm && onUseTemplate) {
                onUseTemplate(pendingTemplate);
            } else {
                onSelectTemplate(pendingTemplate);
            }
        }
        setPendingTemplate(undefined);
        setShouldAdvanceAfterConfirm(false);
    };

    const onCancelSwitch = () => {
        setPendingTemplate(undefined);
        setShouldAdvanceAfterConfirm(false);
    };

    const handleStartBuilding = () => {
        if (selectedTemplate || initialTemplateId) {
            setPendingStartBuilding(true);
        } else {
            onStartBuilding();
        }
    };

    const handleConfirmDialog = async () => {
        if (pendingStartBuilding) {
            if (onClearTemplate) {
                await onClearTemplate();
            }
            onStartBuilding();
            setPendingStartBuilding(false);
        } else {
            onConfirmSwitch();
        }
    };

    return (
        <PageContainer>
            <Container direction={'row'}>
                <Card
                    className={
                        'flex flex-col items-center gap-4 self-start px-16 py-8 sm:max-w-[430px] sm:flex-row xl:max-w-[600px] xl:py-14'
                    }
                    borderRadius={'2xl'}
                >
                    <Heading
                        level={3}
                        weight={'medium'}
                        className={'pr-5'}
                    >
                        {isSelfEvaluation
                            ? t('cycleEvaluationSelectTemplate.headerCard.title')
                            : t('evaluation360SelectTemplate.headerCard.title')}
                    </Heading>

                    <Text
                        className={'mt-1'}
                        color={Colors.BASE_600}
                    >
                        {isSelfEvaluation
                            ? t('cycleEvaluationSelectTemplate.headerCard.description')
                            : t('evaluation360SelectTemplate.headerCard.description')}
                    </Text>
                </Card>

                <CreateSurveyTemplateArrowIllustration
                    className={'relative top-20 -left-4 z-2 max-sm:hidden'}
                />
            </Container>

            <Card
                className={'mt-10'}
                borderRadius={'2xl'}
            >
                <div className={'flex flex-row justify-between'}>
                    <Heading
                        level={4}
                        weight={'medium'}
                    >
                        {t('evaluation360SelectTemplate.chooseFromTemplates')}
                    </Heading>

                    {shouldAllowTemplateManagement && (
                        <button
                            onClick={onManageTemplate}
                            className={`flex items-center gap-1 ${defaultHover}`}
                        >
                            {t('cycleEvaluationSelectTemplate.manageTemplates')}

                            <Icon
                                name={'arrow-arrow-right-1'}
                                size={16}
                            />
                        </button>
                    )}
                </div>

                <div className={'mt-6 grid grid-cols-1 gap-6 sm:!grid-cols-2 2xl:!grid-cols-3'}>
                    <NewTemplateCard
                        title={t('evaluation360SelectTemplate.startFromScratch.title')}
                        description={t('evaluation360SelectTemplate.startFromScratch.description')}
                        iconName={'essential-add'}
                        onButtonClick={handleStartBuilding}
                    />

                    {isFetchingTemplates && !isFetchingNextPage ? (
                        <>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <TemplateCardSkeleton key={index} />
                            ))}
                        </>
                    ) : (
                        <>
                            {templates.slice(1).map((template, index) => (
                                <TemplateCard
                                    key={`${template.id} - ${index}`}
                                    description={template.description || ''}
                                    iconName={'electronics-monitor'}
                                    title={replaceDynamicTags({
                                        text: getLocalizedText(template.title),
                                        employee: previewEvaluee,
                                    })}
                                    questionCount={template.questions?.length ?? 0}
                                    isSelected={selectedTemplate?.id === template?.id}
                                    onCardClick={() => handleTemplateClick(template)}
                                    onButtonClick={() => handleUseTemplate(template)}
                                />
                            ))}

                            {isFetchingNextPage && (
                                <>
                                    {Array.from({ length: 6 }).map((_, index) => (
                                        <TemplateCardSkeleton key={index} />
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </div>

                {hasMoreTemplates && (
                    <div className={'flex w-full justify-center'}>
                        <ButtonPrimary
                            onClick={loadMore}
                            className={'mt-8 items-center'}
                            leftIcon={
                                <Icon
                                    className={'mr-2'}
                                    name={'grid-element-1'}
                                    size={20}
                                    color={Colors.WHITE}
                                />
                            }
                        >
                            {t('evaluation360SelectTemplate.browseMoreTemplates')}
                        </ButtonPrimary>
                    </div>
                )}
            </Card>

            <ConfirmDialog
                isOpen={Boolean(pendingTemplate) || pendingStartBuilding}
                title={t('evaluation360SelectTemplate.switchTemplateConfirm.title')}
                message={t('evaluation360SelectTemplate.switchTemplateConfirm.message')}
                confirmLabel={t('evaluation360SelectTemplate.switchTemplateConfirm.confirm')}
                cancelLabel={t('evaluation360SelectTemplate.switchTemplateConfirm.cancel')}
                onConfirm={handleConfirmDialog}
                onCancel={() => {
                    setPendingStartBuilding(false);
                    onCancelSwitch();
                }}
                isDestructive
                dataTestId={'switch-template-confirm-dialog'}
            />
        </PageContainer>
    );
};
